import { useEffect, useState } from 'react';
import { Box, Typography, Grid, Card, CardContent, Paper, Stack, Divider, CircularProgress, TextField, Button } from '@mui/material';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  LineElement,
  BarElement,
  PointElement
} from 'chart.js';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import { Link } from 'react-router-dom';
import apiClient from '../../utils/apiClient';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, LineElement, BarElement, PointElement);

const Dashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [originalDashboard, setOriginalDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Filter logic for trend data
  const fetchDashboard = async (params = {}) => {
    setLoading(true);
    try {
      const res = await apiClient.get('/dashboard/dashboard', { params });

      if (res.data && res.data.status) {
        setDashboard(res.data.data);
        setOriginalDashboard(res.data.data);
      } else {
        setDashboard(null);
        setOriginalDashboard(null);
      }
    } catch (err) {
      console.log(err);
      setDashboard(null);
      setOriginalDashboard(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchDashboard();
  }, []);

  const handleSearch = () => {
    if (!originalDashboard) return;
    setLoading(true);
    setTimeout(() => {
      const params = {};
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      fetchDashboard(params);

      setDashboard({
        ...originalDashboard, bookings: {...originalDashboard.bookings}
      });
      setLoading(false);
    }, 300);
  };

  // Prepare chart data from API response
  const lineData = dashboard ? {
    labels: dashboard.bookings?.trend?.map(t => t.date) || [],
    datasets: [{
      label: 'Booking Trend',
      data: dashboard.bookings?.trend?.map(t => t.count) || [],
      borderColor: '#3b82f6',
      backgroundColor: 'rgba(59, 130, 246, 0.2)',
      fill: true
    }]
  } : { labels: [], datasets: [] };

  const barData = dashboard ? {
    labels: ['Online', 'Offline', 'Waiting Verification'],
    datasets: [{
      label: 'Technician Status',
      data: [
        dashboard.technicians?.online || 0,
        dashboard.technicians?.offline || 0,
        dashboard.technicians?.waitingVerification || 0,
      ],
      backgroundColor: ['#10b981', '#ef4444', '#fbbf24']
    }]
  } : { labels: [], datasets: [] };

  const doughnutData = dashboard ? {
    labels: ['Online', 'Offline', 'Waiting Verification'],
    datasets: [{
      label: 'Showroom Status',
      data: [
        dashboard.showrooms?.online || 0,
        dashboard.showrooms?.offline || 0,
        dashboard.showrooms?.waitingVerification || 0
      ],
      backgroundColor: ['#3b82f6', '#6b7280', '#fbbf24']
    }]
  } : { labels: [], datasets: [] };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!dashboard) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant='h6' color='error' align='center'>Failed to load dashboard data.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction='row' spacing={2} mb={2} sx={{ alignItems: 'center', justifyContent: 'space-between', flexDirection: { xs: 'column', sm: 'row' } }}>
        <Typography variant='h5' gutterBottom sx={{ mb: 2 }}>Dashboard</Typography>
        <Stack sx={{ flexDirection: { xs: 'row', sm: 'row' }, gap: 2 }}>
          <TextField
            type='date' label='From' size='small' value={fromDate}
            InputLabelProps={{ shrink: true }}
            onChange={e => setFromDate(e.target.value)}
            inputProps={{ max: toDate || undefined }}
          />
          <TextField
            type='date' label='To' size='small' value={toDate}
            InputLabelProps={{ shrink: true }}
            onChange={e => setToDate(e.target.value)}
            inputProps={{ min: fromDate || undefined }}
          />
          <Button variant='contained' onClick={handleSearch} disabled={loading}>
            {loading ? <CircularProgress size={20} color='inherit' /> : 'Search'}
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={2}>
        {[{
          title: 'Total Technicians', value: dashboard.technicians?.total || 0, link: '/display-technicians'
        }, {
          title: 'Total Showrooms', value: dashboard.showrooms?.total || 0, link: '/display-showrooms'
        }, {
          title: 'Total Bookings', value: dashboard.bookings?.total || 0, link: '/display-bookings'
        }, {
          title: 'Total Earnings', value: `₹ ${dashboard.bookings?.totalEarnings || 0}`
        }].map((card, i) => (
          <Grid item size={{ xs: 12, md: 3, sm: 6 }} key={i}>
            <Link to={card.link} style={{ textDecoration: 'none', color: 'inherit' }}>
              <Card sx={{ p: 2, border: '1px solid #ccc', borderRadius: 2 }}>
                <CardContent>
                  <Typography variant='subtitle2' color='text.secondary'>{card.title}</Typography>
                  <Typography variant='h5'>{card.value}</Typography>
                </CardContent>
              </Card>
            </Link>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} mt={2}>
        <Grid item size={{ xs: 12, sm: 6 }}>
          <Paper sx={{ p: 2, border: '1px solid #ccc' }}>
            <Typography variant='subtitle1' fontWeight={600} mb={2}>Booking Trend</Typography>
            <Line data={lineData} />
          </Paper>
        </Grid>

        <Grid item size={{ xs: 12, sm: 6 }}>
          <Paper sx={{ p: 2, border: '1px solid #ccc' }}>
            <Typography variant='subtitle1' fontWeight={600} mb={2}>Technician Status</Typography>
            <Bar data={barData} />
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} mt={2}>
        <Grid item size={{ xs: 12, sm: 6 }}>
          <Paper sx={{ p: 2, border: '1px solid #ccc', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Typography variant='subtitle1' fontWeight={600} mb={2}>Showroom Status</Typography>
            <Box sx={{ width: '300px', height: '300px' }}>
              <Doughnut data={doughnutData} />
            </Box>
          </Paper>
        </Grid>

        <Grid item size={{ xs: 12, sm: 6 }}>
          <Paper sx={{ p: 2, border: '1px solid #ccc', mb: 2 }}>
            <Typography variant='subtitle1' fontWeight={600} mb={2}>Most Booked Services</Typography>
            <Stack spacing={1} divider={<Divider />}>
              {dashboard.bookings?.mostBookedServices?.map(service => (
                <Typography key={service.id}>
                  {service.name}: {service.bookingCount} bookings
                </Typography>
              ))}
            </Stack>
          </Paper>
          <Paper sx={{ p: 2, border: '1px solid #ccc' }}>
            <Typography variant='subtitle1' fontWeight={600} mb={2}>Least Booked Services</Typography>
            <Stack spacing={1} divider={<Divider />}>
              {dashboard.bookings?.leastBookedServices?.map(service => (
                <Typography key={service.id}>
                  {service.name}: {service.bookingCount} bookings
                </Typography>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
